'use strict'

// functions-shared/catalog/catalogOffering.js
//
// Whether a catalog item is offered for NEW quotes: the "Active (offered for new quotes)" box in Equipment Mgmt,
// stored as costCatalog.isActive. An absent field is active (245 of 247 items had none on 2026-11-03).
//
// Steve, 2026-10-29: "remove the trina 420s and the Trina 425s from all quoting and customer facing templates please"
// (tickets kSqJkaD6UywCB8OvUW0G, gPoCGnpYsX7HS7D8m3Um; docs/features/catalog_active_flag_FEATURE.md).
//
// Inactive narrows only what can be PICKED. Sold jobs, kits, stock, proposals, monitoring and saved designs keep
// reading the item by id, and a picker always keeps a choice already made (a saved value is never hidden,
// src/utils/choiceStoredValues.js).
//
// One rule for every picker (Designer, Allowed Equipment, the workflow checklist) and for the save that turns the box
// off (functions-money/catalog/saveCostItem.js). Pure: no Firebase; the browser imports it as is.

/** Whether new quotes may pick this catalog item. */
function isOfferedForQuotes(item) {
  return !!item && item.isActive !== false
}

/**
 * The items a picker offers: the offered ones, plus any already chosen, in their original order.
 *
 * @param {object[]} items catalog items
 * @param {Iterable<string>|string|null} selectedIds the ids chosen now
 */
function offeredOrSelected(items, selectedIds) {
  const selected = new Set(
    selectedIds == null ? [] : typeof selectedIds === 'string' ? [selectedIds] : [...selectedIds]
  )
  return (Array.isArray(items) ? items : []).filter(item => isOfferedForQuotes(item) || selected.has(item?.id))
}

// A financial product's one-item equipment choices, read by financialProductToBusinessModel.js
// productEquipmentDefault and resolveProductRackingDefault, which refuse an inactive item.
const PRODUCT_DEFAULT_SLOTS = Object.freeze([
  ['defaultPanelId', 'suggested panel'],
  ['defaultInverterId', 'suggested inverter'],
  ['defaultBatteryId', 'suggested battery'],
  ['defaultRackingId', 'suggested roof racking'],
])
// Its Allowed Equipment lists (an id, {id}, or a list of either).
const PRODUCT_ALLOWED_SLOTS = Object.freeze([
  ['requiredPanels', 'allowed panels'],
  ['requiredInverters', 'allowed inverters'],
  ['requiredBatteries', 'allowed batteries'],
])

const entriesOf = value => (value == null || value === '' ? [] : Array.isArray(value) ? value : [value])
const entryId = entry => (typeof entry === 'string' ? entry : entry?.id)

/**
 * Why an item cannot be saved as asked, one sentence each; empty when it can.
 *
 * - An item that is the default for its Sub-Type stays active: every estimate starts from it.
 * - An item an ACTIVE financial product names as a suggested item, or lists in its Allowed Equipment, cannot be
 *   turned off: the product's estimates would refuse it ("must be an available ... catalog item"). Change the product
 *   first. A product that is itself off quotes nothing, so it does not block.
 *
 * @param {object} item the item as it will be saved (stored fields with the save's changes applied), with its id
 * @param {{products?: object[], deactivating?: boolean}} context financial products with ids; `deactivating` when
 *   this save turns the box off (the product checks run only then)
 */
function offeringSaveProblems(item, { products = [], deactivating = false } = {}) {
  if (!item || isOfferedForQuotes(item)) return []
  const problems = []
  const kind = item.subType || 'item'
  if (item.isDefault === true) {
    problems.push(`It is the default ${kind}. Make another ${kind} the default first, or untick Default.`)
  }
  if (!deactivating) return problems
  for (const product of Array.isArray(products) ? products : []) {
    if (!product || product.isActive !== true) continue
    const name = product.displayName || product.name || product.id
    for (const [field, label] of PRODUCT_DEFAULT_SLOTS) {
      if (product[field] === item.id) {
        problems.push(`${name} uses it as its ${label}. Choose another in Financial Products first.`)
      }
    }
    for (const [field, label] of PRODUCT_ALLOWED_SLOTS) {
      if (entriesOf(product[field]).some(entry => entryId(entry) === item.id)) {
        problems.push(`${name} lists it in its ${label}. Remove it there first.`)
      }
    }
  }
  return problems
}

module.exports = {
  isOfferedForQuotes,
  offeredOrSelected,
  offeringSaveProblems,
  PRODUCT_DEFAULT_SLOTS,
  PRODUCT_ALLOWED_SLOTS,
}
